import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { countCriticalDeadlineLots } from '../../../lib/productionTrackingUtils';

const inputClass =
  'w-full min-h-[40px] px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200';

const searchInputClass =
  'w-full min-h-[44px] pl-9 pr-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200';

const ProductionFilters = ({
  filters,
  onFilterChange,
  onResetAll,
  searchQuery,
  onSearchChange,
  machines,
  cases,
  criticalCases = cases,
  readyCount = 0,
  queueActive = false,
  onShowReady,
  showReadyFilter = false,
  showAdvanced = true,
}) => {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  const criticalCount = useMemo(
    () => countCriticalDeadlineLots(criticalCases),
    [criticalCases]
  );

  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value });
  };

  const toggleUrgent = () => {
    handleFilterChange(
      'deadline',
      filters?.deadline === 'urgent' ? 'all' : 'urgent'
    );
  };

  const dateFieldOptions = [
    { value: 'deadline', label: t('productionTracking.filters.dateDeadline') },
    { value: 'start', label: t('productionTracking.filters.dateStart') },
    { value: 'created', label: t('productionTracking.filters.dateCreated') },
    { value: 'updated', label: t('productionTracking.filters.dateUpdated') },
  ];

  const statusOptions = [
    { value: 'all', label: t('productionTracking.filters.allStatus') },
    { value: 'in_progress', label: t('productionTracking.filters.inProgress') },
    { value: 'quality_check', label: t('productionTracking.filters.quality') },
    { value: 'pending', label: t('productionTracking.filters.pending') },
    { value: 'delayed', label: t('productionTracking.filters.delayed') },
    { value: 'completed', label: t('productionTracking.filters.completed') },
  ];

  const priorityOptions = [
    { value: 'all', label: t('productionTracking.filters.allPriority') },
    { value: 'urgent', label: t('productionTracking.filters.priorityUrgent') },
    { value: 'high', label: t('productionTracking.filters.priorityHigh') },
    { value: 'medium', label: t('productionTracking.filters.priorityMedium') },
  ];

  const machineOptions = [
    { value: 'all', label: t('productionTracking.filters.allMachines') },
    ...(machines?.map((m) => ({ value: m?.name, label: m?.name })) || []),
  ];

  const uniqueCases = cases?.filter(
    (c, index, self) =>
      index === self.findIndex((t) => (t.caseId || t.case_id) === (c.caseId || c.case_id))
  );

  const caseOptions = [
    { value: 'all', label: t('productionTracking.filters.allCases') },
    ...(uniqueCases
      ?.filter((c) => (c.caseId || c.case_id) && c.caseNumber)
      .map((c) => ({
        value: c.caseId || c.case_id,
        label: `${c.caseNumber} — ${c.batchNumber || '—'}`,
      })) || []),
  ];

  const hasDateFilter = Boolean(filters?.dateFrom || filters?.dateTo);
  const hasActiveFilters =
    filters?.deadline === 'urgent' ||
    filters?.status !== 'all' ||
    filters?.priority !== 'all' ||
    filters?.machine !== 'all' ||
    filters?.case !== 'all' ||
    hasDateFilter ||
    Boolean(String(searchQuery || '').trim());

  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-4 mb-4 md:mb-6 space-y-3">
      <div className="relative">
        <Icon
          name="Search"
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          aria-hidden
        />
        <input
          type="search"
          className={searchInputClass}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('productionTracking.filters.listSearchPlaceholder')}
          aria-label={t('productionTracking.filters.listSearchPlaceholder')}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {showReadyFilter && (
          <button
            type="button"
            onClick={onShowReady}
            aria-pressed={queueActive}
            aria-label={
              queueActive
                ? t('productionTracking.filters.readyAriaActive', { count: readyCount })
                : t('productionTracking.filters.readyAriaReturn', { count: readyCount })
            }
            className={`min-h-[40px] px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              queueActive
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-border text-foreground hover:bg-muted/50'
            }`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Icon name="Factory" size={14} aria-hidden />
              {t('productionTracking.filters.ready')}
              <span className="tabular-nums px-1.5 py-0.5 rounded-md text-xs font-semibold bg-muted text-foreground">
                {readyCount}
              </span>
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={toggleUrgent}
          aria-pressed={filters?.deadline === 'urgent'}
          className={`min-h-[40px] px-3 py-1.5 rounded-lg text-sm font-medium border cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            filters?.deadline === 'urgent'
              ? 'bg-warning/15 border-warning text-warning'
              : 'border-border text-muted-foreground hover:bg-muted/50'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Icon name="AlertTriangle" size={14} aria-hidden />
            {t('productionTracking.filters.urgentOnly')}
            {criticalCount > 0 && (
              <span
                className={`tabular-nums px-1.5 py-0.5 rounded-md text-xs font-semibold ${
                  filters?.deadline === 'urgent'
                    ? 'bg-warning/20'
                    : 'bg-muted text-foreground'
                }`}
              >
                {criticalCount}
              </span>
            )}
          </span>
        </button>

        <div className="min-w-[150px] flex-1 sm:flex-none sm:w-[180px]">
          <Select
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder={t('productionTracking.filters.status')}
            aria-label={t(
              filters?.status !== 'all'
                ? 'productionTracking.filters.statusWorkshopAria'
                : 'productionTracking.filters.status'
            )}
          />
        </div>

        {showAdvanced && (
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            aria-expanded={moreOpen}
            className="min-h-[40px] px-3 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 cursor-pointer rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t('productionTracking.filters.more')}
            <Icon name={moreOpen ? 'ChevronUp' : 'ChevronDown'} size={14} aria-hidden />
          </button>
        )}

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetAll}
            iconName="RotateCcw"
            className="ml-auto min-h-[40px] cursor-pointer"
          >
            {t('productionTracking.filters.reset')}
          </Button>
        )}
      </div>

      {showReadyFilter && (
        <p className="text-xs text-muted-foreground">
          {t('productionTracking.filters.statusWorkshopHint')}
        </p>
      )}

      {showAdvanced && moreOpen && (
        <div className="space-y-3 pt-3 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Select
              options={priorityOptions}
              value={filters?.priority}
              onChange={(value) => handleFilterChange('priority', value)}
              placeholder={t('productionTracking.filters.priority')}
            />
            <Select
              options={machineOptions}
              value={filters?.machine}
              onChange={(value) => handleFilterChange('machine', value)}
              placeholder={t('productionTracking.filters.machine')}
            />
            <Select
              options={caseOptions}
              value={filters?.case}
              onChange={(value) => handleFilterChange('case', value)}
              placeholder={t('productionTracking.filters.case')}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {t('productionTracking.filters.dateSection')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Select
                label={t('productionTracking.filters.dateField')}
                options={dateFieldOptions}
                value={filters?.dateField || 'deadline'}
                onChange={(value) => handleFilterChange('dateField', value)}
              />
              <div>
                <label
                  htmlFor="filter-date-from"
                  className="text-sm font-medium text-foreground mb-2 block"
                >
                  {t('productionTracking.filters.dateFrom')}
                </label>
                <input
                  id="filter-date-from"
                  type="date"
                  className={inputClass}
                  value={filters?.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="filter-date-to"
                  className="text-sm font-medium text-foreground mb-2 block"
                >
                  {t('productionTracking.filters.dateTo')}
                </label>
                <input
                  id="filter-date-to"
                  type="date"
                  className={inputClass}
                  value={filters?.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </div>
              {hasDateFilter && (
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full min-h-[40px] cursor-pointer"
                    onClick={() =>
                      onFilterChange({
                        ...filters,
                        dateFrom: '',
                        dateTo: '',
                      })
                    }
                  >
                    {t('productionTracking.filters.clearDates')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionFilters;
